import { CorporateDashboardContext } from "contexts/CorporateDashboardContext/CorporateDashboardContext";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { useContext, useMemo } from "react";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import { CircularProgress } from "@mui/material";
import { useStyles } from "../TopFiveFailureCodesAllMills/TopFiveFailureCodesAllMills.styles";
import NoData from "components/NoData/NoData.view";
import ReportDowntimeTopFiveBarChart from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChart";
import { IChartBar } from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChartInterfaces";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum } from "constants/constants";
import { ICorporateReportCardProps } from "../../CorporateReportsInterfaces";
import { IDefaultFailureCodeTranslations } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { useTranslation } from "react-i18next";

const TopFiveSecondaryFailureCodeAllMills = ({ countryCode }: ICorporateReportCardProps) => {
    const classes = useStyles();
    const {
        selectedFailureCode,
        selectedFailureType,
        handleSetSelectedSecondaryFailureCode,
        filters,
        totalDowntimeSeconds,
    } = useContext(CorporateDashboardContext);
    const { corporations } = useContext(CorporateContext);
    const corporateId = corporations[0]?.corporate_id || 0;
    const { i18n } = useTranslation();

    const { downtimeSummaryBySecondaryFailureCodeTotals, loading } =
        CorporationsEntity.GetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard(
            filters,
            corporateId,
            selectedFailureCode?.id,
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryBySecondaryFailureCodeTotals.reduce(
                (
                    acc: {
                        total_seconds: number;
                        total_occurrences: number;
                        codeId: number;
                        name: string;
                        default_failure_code_translations?: IDefaultFailureCodeTranslations[];
                    }[],
                    obj,
                ) => {
                    const codeId = obj.failure_data.failure_code_id;
                    const existingCodeIndex = acc.findIndex((summary) => summary.codeId === codeId);

                    if (!(existingCodeIndex >= 0)) {
                        acc = [
                            ...acc,
                            {
                                codeId: obj.failure_data.failure_code_id,
                                name: obj.failure_data.name,
                                total_occurrences: obj.total_occurrences,
                                total_seconds: obj.total_seconds,
                                default_failure_code_translations: obj.failure_data.default_failure_code_translations,
                            },
                        ];
                    } else {
                        acc[existingCodeIndex].total_occurrences += obj.total_occurrences;
                        acc[existingCodeIndex].total_seconds += obj.total_seconds;
                    }
                    return acc;
                },
                [],
            ),
        [downtimeSummaryBySecondaryFailureCodeTotals],
    );
    //we're mapping the data as Nivo needs it for its graphs
    const graphData: IChartBar[] = useMemo(
        () =>
            downtimeSummaryFormatted
                .sort((a, b) =>
                    filters.displayIn === DisplayInOptionsEnum.MINUTES ||
                    filters.displayIn === DisplayInOptionsEnum.PERCENT
                        ? b.total_seconds - a.total_seconds
                        : b.total_occurrences - a.total_occurrences,
                )
                .slice(0, 5)
                .reverse()
                .map((failureCode) => {
                    const secondsToMinutes =
                        Math.round(failureCode.total_seconds / 60) < 1 ? 1 : Math.round(failureCode.total_seconds / 60);
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return {
                        id: defaultFailureCodeTranslatedName,
                        countryCode: countryCode as string,
                        [`${defaultFailureCodeTranslatedName}Color`]: selectedFailureType?.color_code || "",
                        [defaultFailureCodeTranslatedName]:
                            filters.displayIn === DisplayInOptionsEnum.MINUTES
                                ? secondsToMinutes
                                : filters.displayIn === DisplayInOptionsEnum.PERCENT
                                ? totalDowntimeSeconds
                                    ? (100 * failureCode.total_seconds) / totalDowntimeSeconds
                                    : 0
                                : failureCode.total_occurrences,
                    };
                }),
        [
            downtimeSummaryFormatted,
            filters.displayIn,
            i18n.language,
            countryCode,
            selectedFailureType?.color_code,
            totalDowntimeSeconds,
        ],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeTopFiveBarChart
            data={graphData}
            index="id"
            noPercent={filters.displayIn !== DisplayInOptionsEnum.PERCENT}
            onBarClick={(value) => {
                const secondaryFailureCodeSelected = downtimeSummaryFormatted.find((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return defaultFailureCodeTranslatedName === value;
                });
                if (secondaryFailureCodeSelected)
                    handleSetSelectedSecondaryFailureCode({
                        id: secondaryFailureCodeSelected.codeId,
                        name: value,
                    });
            }}
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default TopFiveSecondaryFailureCodeAllMills;
